import React, { useState, useEffect, useRef } from 'react';
import { Advertisement as AdvertisementType } from '../../types/advertisement';

// Static cache to prevent duplicate API calls
// This will persist across component remounts
const adCache: {
  ad: AdvertisementType | null;
  timestamp: number;
} = { ad: null, timestamp: 0 };

// For TypeScript
declare global {
  interface Window {
    adsbygoogle?: any[];
  }
}

interface AdvertisementProps {
  onUpgradeClick: () => void;
}

// Loading skeleton for the advertisement
const AdSkeleton: React.FC = () => (
  <div className="mb-2 rounded-lg p-4 bg-slate-800 border border-slate-700 text-slate-100 shadow-lg animate-pulse">
    <div className="flex flex-col">
      <div className="h-20 bg-slate-700 rounded mb-2"></div>
      <div className="flex items-center gap-4">
        <div className="flex-shrink-0 bg-slate-700 rounded-md p-2 h-14 w-14"></div>
        <div className="flex-grow">
          <div className="h-5 bg-slate-700 rounded w-1/2 mb-2"></div>
          <div className="h-4 bg-slate-700 rounded w-3/4 mb-2"></div>
          <div className="h-8 bg-slate-700 rounded w-1/3 mt-2"></div>
        </div>
      </div>
    </div>
  </div>
);

const Advertisement: React.FC<AdvertisementProps> = ({ onUpgradeClick }) => {
  const [ad, setAd] = useState<AdvertisementType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const adContainerRef = useRef<HTMLDivElement>(null);
  
  // Open the alpha upgrade page in a new tab
  const handleUpgradeClick = () => {
    // Get the user's email from localStorage if available
    const email = localStorage.getItem('userEmail');
    
    // If we have the email, include it as a URL parameter
    if (email) {
      window.open(`/alpha-upgrade?email=${encodeURIComponent(email)}`, '_blank');
    } else {
      window.open('/alpha-upgrade', '_blank');
    }
  };

  // Fetch the next ad from the backend
  useEffect(() => {
    const fetchNextAd = async () => {
      // Check cache first - use a 30-second cache validity
      const now = Date.now();
      if (adCache.ad && now - adCache.timestamp < 30000) {
        console.log('Advertisement: Using cached ad (cached at ' + new Date(adCache.timestamp).toLocaleTimeString() + ')');
        setAd(adCache.ad);
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        const token = localStorage.getItem('sessionToken');
        const userId = localStorage.getItem('userId');
        
        // Add debugging
        console.log('Advertisement: Fetching next ad');
        console.log('Token exists:', !!token);
        console.log('User ID exists:', !!userId);
        
        if (!token || !userId) {
          console.error('Authentication required: token or userId missing');
          throw new Error('Authentication required');
        }
        
        console.log('Fetching from:', `${import.meta.env.VITE_WORKER_URL}/api/ads/next`);
        
        const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/api/ads/next`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-User-ID': userId
          }
        });
        
        console.log('Response status:', response.status);
        
        if (!response.ok) {
          const errorData = await response.json();
          console.error('Error fetching ad:', errorData);
          throw new Error(errorData.error || 'Failed to fetch advertisement');
        }
        
        const adData = await response.json();
        console.log('Ad data received:', adData);
        
        // Update the cache
        adCache.ad = adData;
        adCache.timestamp = Date.now();
        console.log('Advertisement: Updated cache at ' + new Date(adCache.timestamp).toLocaleTimeString());
        
        setAd(adData);
      } catch (err) {
        console.error('Error fetching ad:', err);
        setError(err instanceof Error ? err.message : 'Failed to load advertisement');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchNextAd();
  }, []);
  
  // For Google AdSense ads, we need to handle script loading and cleanup
  useEffect(() => {
    // Keep track of scripts we add to the DOM for cleanup
    const addedScripts: HTMLScriptElement[] = [];
    
    if (ad && ad.provider === 'google' && adContainerRef.current) {
      try {
        console.log('Setting up Google AdSense ad');
        
        // Create a temporary div to parse the HTML content
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = ad.content;
        
        // Find and execute all script tags EXCEPT the adsbygoogle.push({}) script
        // This is important because we don't want to duplicate the initialization
        const scriptTags = tempDiv.querySelectorAll('script');
        scriptTags.forEach(scriptTag => {
          // Skip any script that contains adsbygoogle.push({}) to prevent double initialization
          if (scriptTag.innerHTML && scriptTag.innerHTML.includes('adsbygoogle.push')) {
            console.log('Skipping adsbygoogle.push script - will be handled by Google automatically');
            return;
          }
          
          const newScript = document.createElement('script');
          
          // Copy all attributes
          Array.from(scriptTag.attributes).forEach(attr => {
            newScript.setAttribute(attr.name, attr.value);
          });
          
          // Copy inline script content
          if (scriptTag.innerHTML) {
            newScript.innerHTML = scriptTag.innerHTML;
          }
          
          // Append to document and track for cleanup
          document.body.appendChild(newScript);
          addedScripts.push(newScript);
          console.log('Added script to document:', newScript.outerHTML.substring(0, 100) + '...');
        });
        
        // We don't need to call adsbygoogle.push({}) manually
        // Google's AdSense code will automatically initialize ads when it loads
        console.log('Letting Google AdSense auto-initialize ads');
      } catch (error) {
        console.error('Error setting up AdSense ad:', error);
      }
    }
    
    // Cleanup function to remove scripts when component unmounts
    return () => {
      console.log('Cleaning up ad resources');
      
      // Remove any scripts we added to prevent memory leaks
      addedScripts.forEach(script => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
          console.log('Removed script from document');
        }
      });
      
      // Clear the ad container's content when unmounting
      if (adContainerRef.current) {
        // Mark this container as no longer initialized
        adContainerRef.current.removeAttribute('data-chad-adsense-initialized');
        
        // We don't need to do anything with the ins elements
        // AdSense will handle them on the next initialization
      }
    };
  }, [ad]);
  
  // Handle ad click for affiliate ads
  const handleAdClick = async () => {
    if (ad && ad.provider === 'affiliate' && ad.targetUrl) {
      try {
        const token = localStorage.getItem('sessionToken');
        const userId = localStorage.getItem('userId');
        
        if (!token || !userId) {
          throw new Error('Authentication required');
        }
        
        // Record the click
        await fetch(`${import.meta.env.VITE_WORKER_URL}/api/ads/${ad.id}/click`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-User-ID': userId
          }
        });
        
        // Open the target URL in a new tab
        window.open(ad.targetUrl, '_blank');
      } catch (error) {
        console.error('Error recording ad click:', error);
      }
    }
  };
  
  // If loading, show skeleton
  if (isLoading) {
    return (
      <div className="flex justify-center w-full">
        <AdSkeleton />
      </div>
    );
  }
  
  // If error or no ad, show upgrade prompt only
  if (error || !ad) {
    return (
      <div className="flex justify-center w-full">
        <div className="mb-2 rounded-lg p-4 bg-slate-800 border border-slate-700 text-slate-100 shadow-lg inline-block max-w-[85%]">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 bg-slate-700 rounded-md p-2">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-10 w-10 text-emerald-500" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M13 10V3L4 14h7v7l9-11h-7z" 
                />
              </svg>
            </div>
            
            <div className="flex-grow">
              <h3 className="text-lg font-semibold text-slate-100">Upgrade to Alpha</h3>
              <p className="text-slate-300 text-sm">
                Enjoy faster responses, no ads, and premium features forever for $200.
              </p>
              <button
                onClick={handleUpgradeClick}
                className="mt-2 px-4 py-1 bg-emerald-500 text-slate-900 rounded-md hover:bg-emerald-400 transition-colors text-sm font-medium"
              >
                Upgrade Now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center w-full">
      <div className={`mb-2 rounded-lg p-4 bg-slate-800 border border-slate-700 text-slate-100 shadow-lg inline-block max-w-[85%] ${
        ad.provider === 'google' ? 'min-w-[250px]' : ''
      }`}>
        <div className="flex flex-col">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs text-slate-400">Sponsored</span>
          </div>
          
          {/* Ad Content */}
          <div
            ref={adContainerRef}
            className={`flex justify-center items-center text-center ${
              ad.provider === 'google' ? 'min-w-[250px]' : ''
            }`}
            key={`ad-container-${ad.id}`} // Add a key to force re-render when ad changes
          >
            {ad && ad.provider === 'google' ? (
              <div
                className="w-full text-center min-w-[250px]"
                dangerouslySetInnerHTML={{ __html: ad.content }}
              />
            ) : (
              <div
                onClick={handleAdClick}
                className="cursor-pointer inline-block mx-auto text-center"
                dangerouslySetInnerHTML={{ __html: ad && ad.content ? ad.content : '' }} 
              />
            )}
          </div>
          
          {/* Always show upgrade option */}
          <div className="mt-2 pt-2 border-t border-slate-700">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 bg-slate-700 rounded-md p-2">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-6 w-6 text-emerald-500" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M13 10V3L4 14h7v7l9-11h-7z" 
                  />
                </svg>
              </div>
              
              <div className="flex-grow">
                <button
                  onClick={handleUpgradeClick}
                  className="w-full px-4 py-1 bg-emerald-500 text-slate-900 rounded-md hover:bg-emerald-400 transition-colors text-sm font-medium"
                >
                  Upgrade to Alpha - No Ads
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Advertisement;
